import { FlaskConical, Droplets, MapPin, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';

export const AdminExtractorsTab = () => {
  const { extractorRegistrations, handleExtractorApproval, deleteExtractor, deleteExtractorRegistration } = useAdmin();

  return (
    <>
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><FlaskConical size={20} /> Extractor Registrations ({extractorRegistrations.length})</h2>
      {extractorRegistrations.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Droplets size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No extractor registrations</h3>
            <p className="text-muted-foreground">Cannabis extractor registrations will appear here for approval.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {extractorRegistrations.map(extractor => (
            <Card key={extractor.id} className={`bg-card border-border ${extractor.status === 'pending' ? 'ring-1 ring-purple-500/30' : ''}`}>
              <CardHeader className="pb-2 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg break-words">{extractor.business_name}</CardTitle>
                  <Badge className={`self-start sm:self-auto shrink-0 ${extractor.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : extractor.status === 'approved' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>{extractor.status}</Badge>
                </div>
                <CardDescription className="text-xs sm:text-sm break-all">{extractor.owner_name} - {extractor.email}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground"><MapPin size={14} className="shrink-0" /><span>{extractor.city}, Minnesota</span></div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-xs">{extractor.extraction_types}</Badge>
                  {extractor.accepts_wholesale && <Badge variant="outline" className="text-xs">Wholesale</Badge>}
                  {extractor.white_label && <Badge variant="outline" className="text-xs">White Label</Badge>}
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{extractor.description}</p>
                {extractor.products && <p className="text-xs text-purple-400 break-words">Products: {extractor.products}</p>}
                {extractor.phone && <p className="text-xs text-muted-foreground">Phone: {extractor.phone}</p>}
                {extractor.status === 'pending' && (
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handleExtractorApproval(extractor.id, 'approve')}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                    <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleExtractorApproval(extractor.id, 'reject')}><XCircle size={14} className="mr-1" /> Reject</Button>
                  </div>
                )}
                {extractor.status === 'approved' && extractor.extractor_id && (
                  <div className="flex flex-col gap-2 pt-2">
                    <Button size="sm" variant="outline" className="w-full" asChild><a href={`/extractor/${extractor.extractor_id}`} target="_blank" rel="noopener noreferrer">View Profile</a></Button>
                    <Button size="sm" variant="destructive" className="w-full" onClick={() => deleteExtractor(extractor.extractor_id)}><Trash2 size={14} className="mr-1" /> Delete Extractor</Button>
                  </div>
                )}
                {(extractor.status === 'rejected' || (extractor.status !== 'pending' && !extractor.extractor_id)) && (
                  <Button size="sm" variant="destructive" className="w-full mt-2" onClick={() => deleteExtractorRegistration(extractor.id)}><Trash2 size={14} className="mr-1" /> Delete Registration</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
